import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

interface Option {
    value: string;
    label: string;
    subLabel?: string;
    group?: string;
}

interface SearchableSelectProps {
    options: Option[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    required?: boolean;
    className?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = "Select...",
    label,
    disabled = false,
    required = false,
    className = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Filtered options based on search query
    const filteredOptions = useMemo(() => {
        if (!searchQuery) return options;
        const query = searchQuery.toLowerCase();
        return options.filter(opt =>
            opt.label.toLowerCase().includes(query) ||
            (opt.subLabel && opt.subLabel.toLowerCase().includes(query)) ||
            (opt.group && opt.group.toLowerCase().includes(query))
        );
    }, [searchQuery, options]);

    // Group options for rendering
    const groupedOptions = useMemo(() => {
        const groups: Record<string, Option[]> = {};
        const noGroup: Option[] = [];

        filteredOptions.forEach(opt => {
            if (opt.group) {
                if (!groups[opt.group]) groups[opt.group] = [];
                groups[opt.group].push(opt);
            } else {
                noGroup.push(opt);
            }
        });

        return { groups, noGroup };
    }, [filteredOptions]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => opt.value === value);

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchQuery('');
    };

    const clearSelection = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange('');
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <div
                className={`
                    relative w-full border rounded-lg px-3 py-2 text-sm bg-white cursor-pointer flex items-center justify-between transition-all
                    ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'hover:border-primary'}
                    ${isOpen ? 'ring-2 ring-primary/20 border-primary shadow-sm' : 'border-gray-200'}
                `}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                {selectedOption ? (
                    <div className="flex flex-col truncate pr-6">
                        <span className="font-medium text-gray-900">{selectedOption.label}</span>
                        {selectedOption.subLabel && (
                            <span className="text-xs text-gray-500">{selectedOption.subLabel}</span>
                        )}
                    </div>
                ) : (
                    <span className="text-gray-400">{placeholder}</span>
                )}

                <div className="flex items-center gap-1">
                    {value && !disabled && (
                        <button
                            type="button"
                            onClick={clearSelection}
                            className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </div>

            {isOpen && (
                <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
                    <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                ref={inputRef}
                                type="text"
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                                placeholder="Buscar..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>

                    <div className="overflow-y-auto flex-1 custom-scrollbar min-h-0">
                        {filteredOptions.length > 0 ? (
                            <>
                                {/* Rendering Options without groups first */}
                                {groupedOptions.noGroup.map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`
                                            w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex flex-col border-b border-gray-50 last:border-0
                                            ${value === opt.value ? 'bg-primary/5 text-primary border-primary/10' : 'text-gray-700'}
                                        `}
                                        onClick={() => handleSelect(opt.value)}
                                    >
                                        <span className={`font-medium ${value === opt.value ? 'text-primary' : ''}`}>{opt.label}</span>
                                        {opt.subLabel && (
                                            <span className="text-xs opacity-70">{opt.subLabel}</span>
                                        )}
                                    </button>
                                ))}

                                {/* Rendering Groups */}
                                {Object.entries(groupedOptions.groups).map(([groupName, opts]) => (
                                    <div key={groupName}>
                                        <div className="px-4 py-1.5 bg-gray-50/50 text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-50">
                                            {groupName}
                                        </div>
                                        {opts.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                className={`
                                                    w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex flex-col border-b border-gray-50 last:border-0
                                                    ${value === opt.value ? 'bg-primary/5 text-primary border-primary/10' : 'text-gray-700'}
                                                `}
                                                onClick={() => handleSelect(opt.value)}
                                            >
                                                <span className={`font-medium ${value === opt.value ? 'text-primary' : ''}`}>{opt.label}</span>
                                                {opt.subLabel && (
                                                    <span className="text-xs opacity-70">{opt.subLabel}</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                ))}
                            </>
                        ) : (
                            <div className="px-4 py-8 text-sm text-gray-400 text-center">
                                <Search className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                                No se encontraron resultados
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
